import { useReadContracts } from "wagmi";
import type { Address } from "viem";
import {
    PIGGY_TOKEN_ABI, PIGGY_STAKING_ABI, LOCKED_LP_STAKING_ABI, PIGGY_ORIGINALS_ABI,
} from "@/lib/abis";
import { ADDRESSES } from "@/lib/config";

const DEAD: Address = "0x000000000000000000000000000000000000dEaD";

const fallback = {
    totalSupply: 1_000_000_000n * 10n ** 18n,
    burned: 0n,
    totalLpReceived: 0n,
    totalPiggyStakingReceived: 0n,
    nftMinted: 0n,
    nftMaxSupply: 5_000n,
};

export function useHallStats() {
    const piggy = ADDRESSES.PIGGY_TOKEN;
    const lp = ADDRESSES.LOCKED_LP_STAKING;
    const pstake = ADDRESSES.PIGGY_STAKING;
    const originals = ADDRESSES.PIGGY_ORIGINALS;

    const reads = useReadContracts({
        allowFailure: true,
        contracts: [
            ...(piggy
                ? [
                      { address: piggy, abi: PIGGY_TOKEN_ABI, functionName: "totalSupply" } as const,
                      { address: piggy, abi: PIGGY_TOKEN_ABI, functionName: "balanceOf", args: [DEAD] } as const,
                  ]
                : []),
            ...(lp
                ? [{ address: lp, abi: LOCKED_LP_STAKING_ABI, functionName: "totalReceived" } as const]
                : []),
            ...(pstake
                ? [{ address: pstake, abi: PIGGY_STAKING_ABI, functionName: "totalReceived" } as const]
                : []),
            ...(originals
                ? [
                      { address: originals, abi: PIGGY_ORIGINALS_ABI, functionName: "totalMinted" } as const,
                      { address: originals, abi: PIGGY_ORIGINALS_ABI, functionName: "MAX_SUPPLY" } as const,
                  ]
                : []),
        ],
        query: { refetchInterval: 30_000 },
    });

    if (!piggy || !reads.data) return { stats: fallback, live: false };

    let i = 0;
    const totalSupply = (reads.data[i++]?.result as bigint | undefined) ?? fallback.totalSupply;
    const burned      = (reads.data[i++]?.result as bigint | undefined) ?? 0n;
    const lpRcv       = lp ? ((reads.data[i++]?.result as bigint | undefined) ?? 0n) : 0n;
    const psRcv       = pstake ? ((reads.data[i++]?.result as bigint | undefined) ?? 0n) : 0n;
    const minted      = originals ? ((reads.data[i++]?.result as bigint | undefined) ?? 0n) : 0n;
    const maxSupply   = originals ? ((reads.data[i++]?.result as bigint | undefined) ?? fallback.nftMaxSupply) : fallback.nftMaxSupply;

    return {
        stats: {
            totalSupply,
            burned,
            totalLpReceived: lpRcv,
            totalPiggyStakingReceived: psRcv,
            nftMinted: minted,
            nftMaxSupply: maxSupply,
        },
        live: true,
    };
}
